import {Button, IntegerInput, NumberInput, Segment, Textarea, Tooltip} from 'platform/components';
import {Grid, GridItem, HStack, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {CompositionFont} from '@dms/api/sales';
import {CompositionTextLayer, FinancialProductType} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {compositionEditorLimits} from '../constants/compositionEditorLimits';
import {getFontFaceToggles} from '../utils/getFontFaceToggles';
import {resolveFontFace} from '../utils/resolveFontFace';
import {FontFacePatch, toFontFacePatch} from '../utils/toFontFacePatch';
import {ColorField} from './ColorField';
import {FinancingFieldPicker} from './FinancingFieldPicker';
import {FontPicker} from './FontPicker';

interface TextLayerFieldsProps extends TestIdProps {
  layer: CompositionTextLayer;
  fonts: CompositionFont[];
  isLoadingFonts: boolean;
  financialProductType?: FinancialProductType;
  onChange: (patch: Partial<CompositionTextLayer>) => void;
  onFontDeleted: (fontId: string) => void;
}

const MIN_FONT_SIZE = 1;
const MIN_LINE_HEIGHT = 0.5;
const MAX_LINE_HEIGHT = 4;
const LINE_HEIGHT_STEP = 0.1;

const ALIGN_OPTIONS: {value: CompositionTextLayer['align']; label: string}[] = [
  {value: 'left', label: i18n.t('entity.watermark.composition.labels.alignLeft')},
  {value: 'center', label: i18n.t('entity.watermark.composition.labels.alignCenter')},
  {value: 'right', label: i18n.t('entity.watermark.composition.labels.alignRight')},
];

type VerticalAlign = NonNullable<CompositionTextLayer['verticalAlign']>;

const VERTICAL_ALIGN_OPTIONS: {value: VerticalAlign; label: string}[] = [
  {value: 'top', label: i18n.t('entity.watermark.composition.labels.alignTop')},
  {value: 'middle', label: i18n.t('entity.watermark.composition.labels.alignVCenter')},
  {value: 'bottom', label: i18n.t('entity.watermark.composition.labels.alignBottom')},
];

export function TextLayerFields(props: TextLayerFieldsProps) {
  const insertToken = (token: string) => {
    const separator = props.layer.text.length === 0 || props.layer.text.endsWith(' ') ? '' : ' ';
    props.onChange({text: `${props.layer.text}${separator}${token}`});
  };
  const canInsertToken = props.layer.text.length < compositionEditorLimits.maxTextLength;

  const fontFace = getFontFaceToggles(props.layer, props.fonts);
  const boldTooltip = fontFace.bold.isActive
    ? i18n.t('entity.watermark.composition.labels.regularFaceMissing')
    : i18n.t('entity.watermark.composition.labels.boldFaceMissing');
  const italicTooltip = match(fontFace)
    .with({face: null}, () => i18n.t('entity.watermark.composition.labels.builtInFontNoItalic'))
    .with({italic: {isActive: true}}, () =>
      i18n.t('entity.watermark.composition.labels.uprightFaceMissing')
    )
    .otherwise(() => i18n.t('entity.watermark.composition.labels.italicFaceMissing'));

  const applyFontFacePatch = (patch: FontFacePatch | null) => {
    if (isNotNil(patch)) {
      props.onChange(patch);
    }
  };

  // A new family keeps the Bold and Italic state when it has that face, else takes any face of it.
  const handleFamilyChange = (family: string | null) => {
    if (isNil(family)) {
      props.onChange({fontId: null});
      return;
    }
    const face =
      resolveFontFace(props.fonts, family, {
        isBold: fontFace.bold.isActive,
        isItalic: fontFace.italic.isActive,
      }) ?? props.fonts.find((font) => font.family === family);
    if (isNotNil(face)) {
      props.onChange(toFontFacePatch(face));
    }
  };

  return (
    <VStack spacing={3}>
      <Textarea
        label={i18n.t('entity.watermark.composition.labels.text')}
        value={props.layer.text}
        minRows={2}
        maxLength={compositionEditorLimits.maxTextLength}
        onChange={(value) => props.onChange({text: value ?? ''})}
        data-testid={suffixTestId('text', props)}
      />
      <Show when={isNotNil(props.financialProductType)}>
        <FinancingFieldPicker
          financialProductType={props.financialProductType ?? 'STANDARD'}
          isDisabled={!canInsertToken}
          onInsert={insertToken}
          data-testid={suffixTestId('insertField', props)}
        />
      </Show>
      <FontPicker
        face={fontFace.face}
        fonts={props.fonts}
        isLoadingFonts={props.isLoadingFonts}
        onFamilyChange={handleFamilyChange}
        onFontUploaded={props.onChange}
        onFontDeleted={props.onFontDeleted}
        data-testid={suffixTestId('fontPicker', props)}
      />
      <Grid columns={2}>
        <GridItem>
          <IntegerInput
            label={i18n.t('entity.watermark.composition.labels.fontSize')}
            value={props.layer.fontSize}
            minStepperValue={MIN_FONT_SIZE}
            isStepperVisible
            onChange={(value) => props.onChange({fontSize: Math.max(MIN_FONT_SIZE, value ?? 0)})}
            data-testid={suffixTestId('fontSize', props)}
          />
        </GridItem>
        <GridItem>
          <NumberInput
            label={i18n.t('entity.watermark.composition.labels.lineHeight')}
            value={props.layer.lineHeight}
            step={LINE_HEIGHT_STEP}
            decimalPlaces={1}
            minStepperValue={MIN_LINE_HEIGHT}
            maxStepperValue={MAX_LINE_HEIGHT}
            isStepperVisible
            onChange={(value) =>
              props.onChange({
                lineHeight: Math.min(
                  MAX_LINE_HEIGHT,
                  Math.max(MIN_LINE_HEIGHT, value ?? MIN_LINE_HEIGHT)
                ),
              })
            }
            data-testid={suffixTestId('lineHeight', props)}
          />
        </GridItem>
      </Grid>
      <HStack spacing={2}>
        <Tooltip label={boldTooltip} isDisabled={isNotNil(fontFace.bold.patch)}>
          <Button
            variant={fontFace.bold.isActive ? 'primary' : 'secondary'}
            title={i18n.t('entity.watermark.composition.labels.bold')}
            isDisabled={props.isLoadingFonts || isNil(fontFace.bold.patch)}
            onClick={() => applyFontFacePatch(fontFace.bold.patch)}
            data-testid={suffixTestId('bold', props)}
          />
        </Tooltip>
        <Tooltip label={italicTooltip} isDisabled={isNotNil(fontFace.italic.patch)}>
          <Button
            variant={fontFace.italic.isActive ? 'primary' : 'secondary'}
            title={i18n.t('entity.watermark.composition.labels.italic')}
            isDisabled={props.isLoadingFonts || isNil(fontFace.italic.patch)}
            onClick={() => applyFontFacePatch(fontFace.italic.patch)}
            data-testid={suffixTestId('italic', props)}
          />
        </Tooltip>
      </HStack>
      <Segment<CompositionTextLayer['align']>
        label={i18n.t('entity.watermark.composition.labels.textAlign')}
        value={props.layer.align}
        options={ALIGN_OPTIONS}
        onChange={(align) => props.onChange({align})}
        data-testid={suffixTestId('align', props)}
      />
      <Segment<VerticalAlign>
        label={i18n.t('entity.watermark.composition.labels.verticalAlign')}
        value={props.layer.verticalAlign ?? 'top'}
        options={VERTICAL_ALIGN_OPTIONS}
        onChange={(verticalAlign) => props.onChange({verticalAlign})}
        data-testid={suffixTestId('verticalAlign', props)}
      />
      <ColorField
        label={i18n.t('entity.watermark.composition.labels.color')}
        value={props.layer.color}
        onChange={(color) => props.onChange({color})}
        data-testid={suffixTestId('color', props)}
      />
    </VStack>
  );
}
